import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Image,
  Heading,
  Flex,
} from '@everlywell/ui-kit';
import { CaretDown } from '@phosphor-icons/react';
import React, { useState } from 'react';

export interface AccordionCardProps {
  backgroundColor: string;
  cta?: {
    text?: string;
    url?: string;
  };
  heading: string;
  description: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
      html: string;
    };
  };
  image?: {
    title?: string;
    url?: string;
  };
}

export default function AccordionCard({
  backgroundColor,
  cta,
  heading,
  description,
  image,
}: AccordionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Accordion
      allowToggle
      height="100%"
      width="100%"
      overflow="hidden"
      backgroundColor={isExpanded ? '#F2F2ED' : backgroundColor}
      borderRadius="xl"
      transition="background-color 0.2s ease-in-out"
      display="flex"
      flexDirection="column"
      justifyContent="flex-end"
      p={5}
    >
      <AccordionItem border="none" height={isExpanded ? 'auto' : '100%'}>
        {({ isExpanded }: { isExpanded: boolean }) => (
          <>
            <Box
              width="100%"
              height={isExpanded ? 'auto' : '100%'}
              display="flex"
              flexDirection="column"
              justifyContent={isExpanded ? 'flex-end' : 'space-between'}
            >
              {!isExpanded && image?.url && (
                <Box marginBottom={6} borderRadius="xl" overflow="hidden">
                  <Image
                    src={image.url}
                    alt={image.title || ''}
                    width="100%"
                    maxWidth="140px"
                    height="auto"
                  />
                </Box>
              )}

              <AccordionButton
                padding={0}
                _hover={{ backgroundColor: 'transparent' }}
                marginBottom={4}
                onClick={handleToggle}
              >
                <Flex width="100%" direction="column" gap={6}>
                  <Heading fontSize="32px" fontWeight={400}>
                    {heading}
                  </Heading>
                  <Flex
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box fontSize="15px" fontWeight="medium">
                      {cta?.text || 'Learn more'}
                    </Box>
                    <CaretDown
                      size={20}
                      color="#28724F"
                      weight="bold"
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                    />
                  </Flex>
                </Flex>
              </AccordionButton>
            </Box>

            <AccordionPanel
              padding={0}
              opacity={isExpanded ? 1 : 0}
              height={isExpanded ? 'auto' : 0}
              transition="all 0.2s ease-in-out"
              pr={2}
              visibility={isExpanded ? 'visible' : 'hidden'}
            >
              {description?.childMarkdownRemark?.rawMarkdownBody ? (
                <div
                  data-testid="accordion-description"
                  dangerouslySetInnerHTML={{
                    __html: description.childMarkdownRemark.html,
                  }}
                />
              ) : null}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
}
